import React, { useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { DndContext, PointerSensor, TouchSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, TextField, Typography } from '@mui/material';
import { Delete } from '@mui/icons-material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import SwapVertIcon from '@mui/icons-material/SwapVert';

export function SortableSection({ id, children }) {
    const { setNodeRef, transform, transition, isDragging } = useSortable({ id });

    return (
        <Box
            ref={setNodeRef}
            sx={{
                mb: 3, border: '1px solid', borderColor: isDragging ? 'primary.main' : 'divider',
                borderRadius: 2,
                transform: CSS.Transform.toString(transform),
                transition,
                opacity: isDragging ? 0.5 : 1,
            }}
        >
            <Box sx={{ p: 2 }}>
                {children}
            </Box>
        </Box>
    );
}

// ── Section name row used inside the reorder modal ───────────────────────────

function SortableSectionNameRow({ id, name }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
    return (
        <Box
            ref={setNodeRef}
            sx={{
                display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 1.25,
                border: '1px solid', borderColor: isDragging ? 'primary.main' : 'divider',
                borderRadius: 1, mb: 1, bgcolor: 'background.paper',
                transform: CSS.Transform.toString(transform),
                transition,
                opacity: isDragging ? 0.4 : 1,
            }}
        >
            <IconButton size="small" {...attributes} {...listeners}
                sx={{ cursor: 'grab', color: 'text.disabled', '&:active': { cursor: 'grabbing' }, touchAction: 'none' }}>
                <DragIndicatorIcon fontSize="small" />
            </IconButton>
            <Typography variant="body2" sx={{ flex: 1 }}>
                {name || <em style={{ color: '#aaa' }}>Unnamed section</em>}
            </Typography>
        </Box>
    );
}

export function SectionReorderModal({ open, onClose, sections, onApply, title }) {
    const [order, setOrder] = useState(sections.map((_, i) => i));

    // Reset when modal opens
    React.useEffect(() => {
        if (open) setOrder(sections.map((_, i) => i));
    }, [open, JSON.stringify(sections.map(s => s.name))]); // eslint-disable-line react-hooks/exhaustive-deps

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
    );

    const handleDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id) return;
        const oldIdx = order.indexOf(Number(active.id));
        const newIdx = order.indexOf(Number(over.id));
        setOrder(arrayMove(order, oldIdx, newIdx));
    };

    const handleApply = () => {
        onApply(order);
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SwapVertIcon fontSize="small" />
                Reorder {title} Sections
            </DialogTitle>
            <DialogContent>
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={order.map(String)} strategy={verticalListSortingStrategy}>
                        {order.map((origIdx) => (
                            <SortableSectionNameRow
                                key={origIdx}
                                id={String(origIdx)}
                                name={sections[origIdx]?.name}
                            />
                        ))}
                    </SortableContext>
                </DndContext>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="inherit">Cancel</Button>
                <Button onClick={handleApply} variant="contained">Apply</Button>
            </DialogActions>
        </Dialog>
    );
}

export function SortableIngredientRow({ id, ingredientObj, index, catIndex, showDelete, errors, onChange, onDelete }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

    return (
        <Box
            ref={setNodeRef}
            sx={{
                mb: 2, display: 'flex', alignItems: 'flex-start', gap: 0.5,
                transform: CSS.Transform.toString(transform),
                transition,
                opacity: isDragging ? 0.4 : 1,
                bgcolor: isDragging ? 'action.hover' : 'transparent',
                borderRadius: 1,
            }}
        >
            {/* Drag handle */}
            <IconButton size="small" {...attributes} {...listeners}
                sx={{ mt: 0.5, cursor: 'grab', color: 'text.disabled', '&:active': { cursor: 'grabbing' }, touchAction: 'none', flexShrink: 0 }}>
                <DragIndicatorIcon fontSize="small" />
            </IconButton>

            {/* Fields */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
                {/* Qty + Unit row */}
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <TextField label="Qty" variant="outlined" value={ingredientObj.quantity}
                        onChange={(e) => onChange(e.target.value, 'quantity', catIndex, index)}
                        size="small" sx={{ flex: 1 }} />
                    <TextField label="Unit" variant="outlined" value={ingredientObj.unit}
                        onChange={(e) => onChange(e.target.value, 'unit', catIndex, index)}
                        size="small" sx={{ flex: 1 }} />
                </Box>
                {/* Ingredient row */}
                <TextField label={`Ingredient ${index + 1}`} variant="outlined" fullWidth
                    value={ingredientObj.ingredient}
                    onChange={(e) => onChange(e.target.value, 'ingredient', catIndex, index)}
                    error={!!errors?.ingredients && !ingredientObj.ingredient?.trim()}
                    helperText={!!errors?.ingredients && !ingredientObj.ingredient?.trim() ? errors.ingredients : ''}
                    size="small" />
            </Box>

            {/* Delete */}
            {showDelete && (
                <IconButton size="small" sx={{ mt: 0.5, flexShrink: 0 }} onClick={() => onDelete(catIndex, index)}>
                    <Delete fontSize="small" />
                </IconButton>
            )}
        </Box>
    );
}

export function SortableDirectionRow({ id, direction, index, catIndex, showDelete, errors, onChange, onDelete }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

    return (
        <Box
            ref={setNodeRef}
            sx={{
                display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1,
                transform: CSS.Transform.toString(transform),
                transition,
                opacity: isDragging ? 0.4 : 1,
                bgcolor: isDragging ? 'action.hover' : 'transparent',
                borderRadius: 1,
            }}
        >
            <IconButton size="small" {...attributes} {...listeners}
                sx={{ mt: 1, cursor: 'grab', color: 'text.disabled', '&:active': { cursor: 'grabbing' }, touchAction: 'none' }}>
                <DragIndicatorIcon fontSize="small" />
            </IconButton>
            <TextField
                label={`Step ${index + 1}`} variant="outlined" fullWidth value={direction}
                onChange={(e) => onChange(e.target.value, catIndex, index)}
                error={!!errors?.directions && !direction?.trim()}
                helperText={!!errors?.directions && !direction?.trim() ? errors.directions : ''}
                multiline minRows={1}
            />
            {showDelete && (
                <IconButton size="small" sx={{ mt: 1 }} onClick={() => onDelete(catIndex, index)}>
                    <Delete fontSize="small" />
                </IconButton>
            )}
        </Box>
    );
}
